import React from "react";
import { Calendar, Clock, MapPin, DollarSign, FileText } from "lucide-react";

/**
 * ScheduledTaskCard Component
 * 
 * Displays a scheduled task card on the left side of the schedule screen.
 * Shows task details including service info, date/time, location, price, and status.
 * 
 * @param {Object} props - Component props
 * @param {Object} props.task - Task object containing all task details
 * @param {Function} props.onClick - Handler function when card is clicked
 * @returns {JSX.Element} Scheduled task card
 */
const ScheduledTaskCard = ({ task, onClick }) => {
    // Define status badge styling based on task status
    const getStatusBadgeClass = (status) => {
        const statusClasses = {
            pending_offers: "bg-yellow-100 text-yellow-700 border-yellow-300 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border-yellow-800",
            searching: "bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800",
            handyman_selected: "bg-indigo-100 text-indigo-700 border-indigo-300 dark:bg-indigo-900/30 dark:text-indigo-400 dark:border-indigo-800",
            scheduled: "bg-green-100 text-green-700 border-green-300 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800",
            in_progress: "bg-purple-100 text-purple-700 border-purple-300 dark:bg-purple-900/30 dark:text-purple-400 dark:border-purple-800",
            approved: "bg-emerald-100 text-emerald-700 border-emerald-300 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800",
            completed: "bg-emerald-100 text-emerald-700 border-emerald-300 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800",
            cancelled: "bg-red-100 text-red-700 border-red-300 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800",
            disputed: "bg-orange-100 text-orange-700 border-orange-300 dark:bg-orange-900/30 dark:text-orange-400 dark:border-orange-800",
            payment_received: "bg-emerald-100 text-emerald-700 border-emerald-300 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800",
            expired: "bg-gray-100 text-gray-700 border-gray-300 dark:bg-gray-900/30 dark:text-gray-400 dark:border-gray-800",
            on_the_way: "bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800",
            arrived: "bg-purple-100 text-purple-700 border-purple-300 dark:bg-purple-900/30 dark:text-purple-400 dark:border-purple-800",
            work_started: "bg-purple-100 text-purple-700 border-purple-300 dark:bg-purple-900/30 dark:text-purple-400 dark:border-purple-800",
        };

        return statusClasses[status] || "bg-gray-100 text-gray-700 border-gray-300 dark:bg-gray-900/30 dark:text-gray-400 dark:border-gray-800";
    };

    // Format status text for display (convert snake_case to Title Case)
    const formatStatus = (status) => {
        return status
            .split("_")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");
    };

    return (
        <div
            onClick={onClick}
            className="rounded-2xl border-2 border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer overflow-hidden group hover:border-[#EFA938]"
        >
            {/* Card Header with gradient background */}
            <div className="px-6 py-4 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border-b border-gray-200 dark:border-slate-700">
                <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                        {/* Service Title */}
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white group-hover:text-[#EFA938] transition-colors">
                            {task.service.title}
                        </h3>
                        {/* Service Category and Subcategory */}
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            {task.service.category} • {task.service.subcategory}
                        </p>
                    </div>

                    {/* Status Badge */}
                    <span
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold border-2 whitespace-nowrap ${getStatusBadgeClass(
                            task.status
                        )}`}
                    >
                        {formatStatus(task.status)}
                    </span>
                </div>
            </div>

            {/* Card Body with task details */}
            <div className="px-6 py-5 space-y-4">
                {/* Scheduled Date and Time */}
                <div className="flex items-start gap-3">
                    <Calendar className="h-5 w-5 text-[#EFA938] mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                        <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                            Scheduled Date & Time
                        </p>
                        <p className="text-sm font-semibold text-gray-900 dark:text-white mt-1">
                            {new Date(task.scheduledDate).toLocaleDateString("en-US", {
                                weekday: "long",
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                            })}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1.5 mt-0.5">
                            <Clock className="h-4 w-4" />
                            {task.scheduledTime}
                        </p>
                    </div>
                </div>

                {/* Location */}
                <div className="flex items-start gap-3">
                    <MapPin className="h-5 w-5 text-[#EFA938] mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                        <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                            Location
                        </p>
                        <p className="text-sm font-semibold text-gray-900 dark:text-white mt-1">
                            {task.location}
                        </p>
                    </div>
                </div>

                {/* Price */}
                <div className="flex items-start gap-3">
                    <DollarSign className="h-5 w-5 text-[#EFA938] mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                        <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                            Budget
                        </p>
                        <p className="text-lg font-bold text-green-600 dark:text-green-400 mt-1">
                            PKR {task.price?.toLocaleString()}
                        </p>
                    </div>
                </div>

                {/* Customer Notes (if available) */}
                {task.customerNotes && (
                    <div className="flex items-start gap-3 pt-3 border-t border-gray-200 dark:border-slate-700">
                        <FileText className="h-5 w-5 text-[#EFA938] mt-0.5 flex-shrink-0" />
                        <div className="flex-1">
                            <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                Notes
                            </p>
                            <p className="text-sm text-gray-700 dark:text-gray-300 mt-1 leading-relaxed">
                                {task.customerNotes}
                            </p>
                        </div>
                    </div>
                )}

                {/* Request Count Badge */}
                <div className="pt-3 border-t border-gray-200 dark:border-slate-700">
                    <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                            Handyman Requests
                        </span>
                        <span className="px-3 py-1 rounded-full bg-[#EFA938] text-white text-xs font-bold">
                            {task.handymanRequests?.length || 0}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ScheduledTaskCard;
